import { Form } from 'antd';
import {
  ButtonFooter,
  GroupButtonFooterModalStyled,
} from 'components/GroupButtonFooterModal';
import Modal from 'components/Modal';
import { CurrencyKeyBoardRef } from 'components/NumberKeyBoard/Currency';
import Text from 'components/Text';
import ticketListActions from 'features/ticketList/services/actions';
import ticketListSelectors from 'features/ticketList/services/selectors';
import { IApiSplitTicketItem } from 'features/ticketList/services/types/ticket';
import { sumBy } from 'lodash';
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useSetLoadingPage } from 'services/UI/LoadingPage';
import { IStaffItemData } from 'services/shop/types/staff';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import { formatCurrency } from 'utils/formatCurrency';
import { roundToDecimal } from 'utils/roundToDecimal';
import ModalChooseStaffFilter from 'widgets/Staff/DropDown/ModalChooseStaffFilter';
import colorTheme from 'constants/color';
import TableSplitTicket from './TableSplitTicket';
import shopSelectors from 'services/shop/selectors';

// Types
type Ref = {
  open: (staffId: string) => void;
};

type Props = {
  v2?: boolean;
};

type FormValues = { staffs: IApiSplitTicketItem[] };

export const useModalSplitTicketV2Ref = () => useRef<Ref>(null);

export const ModalSplitTicketV2 = forwardRef<Ref, Props>(({ v2 }, ref) => {
  const dispatch = useAppDispatch();
  const currencyRef = useRef<CurrencyKeyBoardRef>(null);
  const details = ticketListSelectors.ticketDetailsData.data();
  const [visible, setVisible] = useState(false);
  const [visibleChooseStaff, setVisibleChooseStaff] = useState(false);
  const [form] = Form.useForm<{ staffs: IApiSplitTicketItem[] }>();
  const staffs = Form.useWatch('staffs', form);
  const setPageLoading = useSetLoadingPage();
  const [staffUpdateId, setStaffUpdateId] = useState<string | null>(null);
  const allSetting = shopSelectors.data.allSetting();

  const filterStaff = useCallback(
    (list: IStaffItemData[]) => {
      return list.filter(
        (o) =>
          !o.anyBody && staffs?.findIndex((s) => o.id === s.staffId) === -1,
      );
    },
    [staffs],
  );

  /**
   * Sum current values in each column.
   */
  const getCategoryTotals = useCallback(() => {
    const values = form.getFieldValue('staffs') || [];
    return {
      amount: roundToDecimal(
        sumBy(values, (o: IApiSplitTicketItem) => o.amount || 0),
      ),
      tip: roundToDecimal(
        sumBy(values, (o: IApiSplitTicketItem) => o.tip || 0),
      ),
      discount: roundToDecimal(
        sumBy(values, (o: IApiSplitTicketItem) => o.discount || 0),
      ),
      supplyFee: roundToDecimal(
        sumBy(values, (o: IApiSplitTicketItem) => o.supplyFee || 0),
      ),
    };
  }, [form]);

  const handleAddStaffAutoBalance = useCallback(
    (newStaff: IApiSplitTicketItem) => {
      const newStaffs = [...(form.getFieldValue('staffs') || []), newStaff];
      form.setFieldValue('staffs', newStaffs);
    },
    [form],
  );

  /**
   * Handle removing staff with auto-balance
   */
  const handleRemoveStaffAutoBalance = useCallback(
    (staffIndex: number) => {
      const newStaffs = (form.getFieldValue('staffs') || []).filter(
        (_: IApiSplitTicketItem, index: number) => index !== staffIndex,
      );
      form.setFieldValue('staffs', newStaffs);
    },
    [form],
  );

  /**
   * Create new staff and add with auto-balance
   */
  const handleAddStaff = (o: IStaffItemData) => {
    const newStaff: IApiSplitTicketItem = {
      staffId: o.id,
      amount: 0,
      avatar: o.urlImage,
      staffName: o.nickName ?? o.firstName ?? '',
      discount: 0,
      supplyFee: 0,
      tip: 0,
      service: allSetting?.displayServiceNameSplitMaster ? '' : 'Quick Pay',
    };

    handleAddStaffAutoBalance(newStaff);
  };

  const handleClose = () => setVisible(false);

  const handleFinish = async (values: FormValues) => {
    handleClose();
    setPageLoading(true);
    const payload: IApiSplitTicketItem[] = values.staffs.map(
      (o) =>
        ({
          amount: o.amount,
          discount: o.discount,
          staffId: o.staffId,
          supplyFee: o.supplyFee,
          tip: o.tip,
          itemName: o.service,
        }) as IApiSplitTicketItem,
    );
    dispatch(
      ticketListActions.splitMasterTicket({
        staffUpdateId: staffUpdateId || '',
        billId: details?.billId || '',
        items: payload,
      }),
    );
  };

  useImperativeHandle(ref, () => ({
    open(staffId: string) {
      const dataList = (details?.items || []).map(
        (o) =>
          ({
            staffId: o.staffId,
            amount: sumBy(o.services, (s) => s.price) || 0,
            avatar: o.avatar,
            staffName: o.staffName,
            discount: sumBy(o.services, (s) => s.discount) || 0,
            supplyFee: sumBy(o.services, (s) => s.supplyFee || 0) || 0,
            tip: sumBy(o.services, (s) => s?.tip || 0) || 0,
            service: o.services?.[0]?.itemName || '',
          }) as IApiSplitTicketItem,
      );

      setStaffUpdateId(staffId);
      form.setFieldValue('staffs', dataList);
      setVisible(true);
      setTimeout(() => {
        currencyRef.current?.setValue(formatCurrency(details?.tip || 0));
      }, 100);
    },
  }));


  const handleReset = () => {
    const dataList = (details?.items || []).map(
      (o) =>
        ({
          staffId: o.staffId,
          amount: sumBy(o.services, (s) => s.price) || 0,
          avatar: o.avatar,
          staffName: o.staffName,
          discount: sumBy(o.services, (s) => s.discount) || 0,
          supplyFee: sumBy(o.services, (s) => s.supplyFee || 0) || 0,
          tip: sumBy(o.services, (s) => s?.tip || 0) || 0,
          service: o.services?.[0]?.itemName || '',
        }) as IApiSplitTicketItem,
    );
    form.setFieldValue('staffs', dataList);
  };
  return (
    <Modal
      modalTitle='Split Master'
      okTitle={'Save'}
      closable={false}
      visible={visible}
      onClose={handleClose}
      width={allSetting?.displayServiceNameSplitMaster ? 1680 : 1440}
      disableOk={false}
      noneBodyStyle
      noneFooterStyle
      className='modal-overflow-unset modal-max-height-unset'
      centered
      footer={null}
      forceRender
      v2={v2}
    >
      <Container className={v2 ? 'v2' : ''}>
        <Form form={form} onFinish={handleFinish}>
          <TableSplitTicket
            details={details}
            totals={getCategoryTotals()}
            handleAddStaffAutoBalance={handleAddStaffAutoBalance}
            handleRemoveStaffAutoBalance={handleRemoveStaffAutoBalance}
            setVisibleChooseStaff={setVisibleChooseStaff}
            filterStaff={filterStaff}
          />
          <Form.Item shouldUpdate noStyle>
            {({ getFieldsValue }) => {
              const values: FormValues = getFieldsValue();
              const serviceError = values?.staffs?.some((s) => !s.service)
                ? 'Please select service for all staff'
                : '';
              return (
                <>
                  {serviceError && (
                    <Text
                      style={{
                        opacity: serviceError ? 1 : 0,
                        fontSize: '1.2rem',
                        fontWeight: '500',
                      }}
                      m={1}
                      textAlign='center'
                      variant='CONTENT_1'
                      color='error_6'
                    >
                      {serviceError || ''}
                    </Text>
                  )}
                  <div className='footer-modal'>
                    <GroupButtonFooterModalStyled>
                      <ButtonFooter
                        buttonName='Close'
                        type='DEFAULT'
                        onClick={() => setVisible(false)}
                        cardCashier={v2}
                      />
                      <ButtonFooter
                        buttonName='Reset'
                        type='LIGHT_BLUE'
                        onClick={handleReset}
                        cardCashier={v2}
                      />
                      {/* <ButtonFooter buttonName='Reset' type='SECONDARY' onClick={handleReset} cardCashier={v2} /> */}
                      <ButtonFooter
                        buttonName='Ok'
                        type='PRIMARY'
                        disabled={!!serviceError}
                        onClick={() => form.submit()}
                        cardCashier={v2}
                      />
                    </GroupButtonFooterModalStyled>
                  </div>
                </>
              );
            }}
          </Form.Item>
        </Form>
      </Container>
      <ModalChooseStaffFilter
        filter={filterStaff}
        visible={visibleChooseStaff}
        onChooseStaff={handleAddStaff}
        onClose={() => setVisibleChooseStaff(false)}
      />
    </Modal>
  );
});
ModalSplitTicketV2.displayName = 'ModalSplitTicketV2';

const useModalSplitTicketV2 = (): [React.RefObject<Ref>, JSX.Element] => {
  const ref = useRef<Ref>(null);
  const component = <ModalSplitTicketV2 ref={ref} />;

  return [ref, component];
};
export default useModalSplitTicketV2;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  .currency-container {
    width: 500px;
  }
  .InputViewStyled {
    justify-content: center;
    input {
      width: 80%;
      flex: unset;
    }
  }
  .footer-modal {
    margin-top: 16px;
    box-shadow: 0px -4px 8px 0px #00000026;
    padding: 16px 16px 24px;
  }

  .table-wrapper {
    max-height: 500px;
    overflow: auto;
    padding: 0 12px;
    .table-thead {
      position: sticky;
      top: 0;
      z-index: 99;
      background: #ffffff;
    }

    tfoot {
      position: sticky;
      bottom: 0px;
      z-index: 99;
      tr th {
        background: ${colorTheme.v2_neutral_2};
        padding: 8px 12px;
        .ant-btn {
          box-shadow: none;
        }
      }
    }
  }

  &.v2 {
    zoom: 1.2;
  }

  .medium-text {
    font-family: Poppins;
    font-weight: 500;
  }
`;
